import React from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Label,
  Row,
  Col,
  ModalFooter,
} from 'reactstrap';

import FormField from 'components/AddLocation/FormField';
import AppInputValidation from 'components/App/InputValidation';
import Button from 'components/App/Button';
import SelectValidation from 'components/App/SelectValidation';
import { placeTypeOptions, starOptions } from 'components/AddLocation/StepBasic/config';
import landmarkApi from 'api/landmarkApi';
import marketApi from 'api/marketApi';
import { handleError } from 'utils';
import { LOGIN_TYPE, storageKey } from 'constants/index';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';

import CoordinateMap from 'components/App/CoordinateMap';
import styles from './ModalEditLocation.module.scss';

function ModalEditLocation(props) {
  const {
    isOpenModalEditLocation,
    isLoading,

    validation,
    provinces,
    // districts,
    // wards,

    onToggleModal,
  } = props;

  const mapRef = React.useRef(null);
  const [isMapModalOpen, setIsMapModalOpen] = React.useState(false);
  const toggleMapModal = () => setIsMapModalOpen(!isMapModalOpen);

  const [markets, setMarkets] = React.useState([]);
  const [landmarkOptions, setLandmarkOptions] = React.useState([]);
  const [isLoadingLandmarks, setIsLoadingLandmarks] = React.useState(false);
  const isExtranet = React.useMemo(
    () => localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
    [],
  );

  const formattedProvinces = React.useMemo(() => provinces?.map((province) => ({
    ...province,
    label: province.name,
    value: province.name,
  })).sort((a, b) => a.label.localeCompare(b.label)),
  [provinces]);

  // const formattedDistricts = React.useMemo(() => districts?.map((district) => ({
  //   ...district,
  //   label: district.name,
  //   value: district.name,
  // })).sort((a, b) => a.label.localeCompare(b.label)), [districts]);

  // const formattedWards = React.useMemo(() => wards?.map((ward) => ({
  //   ...ward,
  //   label: ward.name,
  //   value: ward.name,
  // })).sort((a, b) => a.label.localeCompare(b.label)), [wards]);

  const marketOptions = React.useMemo(() => {
    if (!validation.values.province) {
      return [];
    }

    return markets
      .filter((market) => (market.province || market.region) === validation.values.province)
      .map((market) => ({
        ...market,
        label: market.name,
        value: market._id || market.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [markets, validation.values.province]);

  const selectedMarketOption = React.useMemo(
    () => marketOptions.find((item) => item.value === validation.values.market) ?? null,
    [marketOptions, validation.values.market],
  );

  React.useEffect(() => {
    if (!isOpenModalEditLocation || isExtranet) return;

    const fetchMarkets = async () => {
      try {
        const response = await marketApi.getMarkets({
          page: 1,
          limit: 500,
          isPublished: true,
        });

        if (response?.payload?.data) {
          setMarkets(response.payload.data);
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchMarkets();
  }, [isOpenModalEditLocation, isExtranet]);

  React.useEffect(() => {
    if (!isOpenModalEditLocation || !validation.values.market) {
      setLandmarkOptions([]);
      return undefined;
    }

    let cancelled = false;

    const fetchLandmarks = async () => {
      try {
        setIsLoadingLandmarks(true);
        const response = await landmarkApi.getLandmarks({
          market: validation.values.market,
        });
        const rawData = response?.payload?.data ?? [];

        if (!cancelled) {
          setLandmarkOptions(rawData.map((item) => ({
            value: item._id || item.id,
            label: item.name || item.nameEN || '',
          })));
        }
      } catch (error) {
        if (!cancelled) {
          setLandmarkOptions([]);
        }
        handleError(error);
      } finally {
        if (!cancelled) {
          setIsLoadingLandmarks(false);
        }
      }
    };

    fetchLandmarks();

    return () => {
      cancelled = true;
    };
  }, [isOpenModalEditLocation, validation.values.market]);

  const onChangeOption = React.useCallback((name) => (e) => {
    if (name === 'province') {
      validation.setFieldValue('district', '', false);
      validation.setFieldValue('ward', '', false);
      validation.setFieldValue('landmarks', [], false);
      validation.setFieldValue('market', '', false);
    }

    if (name === 'district') {
      validation.setFieldValue('ward', '', false);
    }

    validation.setFieldValue(name, e.value, true);
  }, [validation]);

  const onChangeMarketOption = React.useCallback((e) => {
    validation.setFieldValue('market', e.value, true);
    validation.setFieldValue('landmarks', [], true);
  }, [validation]);

  const CloseButton = React.useMemo(() => (
    <button className={styles.ButtonClose} onClick={onToggleModal} type="button">
      <CloseButtonSvg />
    </button>
  ), [onToggleModal]);

  const onChangeCoordinates = React.useCallback((e) => {
    const stringCoordinates = e.target.value;
    validation.setFieldValue('coordinates', stringCoordinates);
    mapRef.current.panTo({
      lat: parseFloat(stringCoordinates.split(',')[0]), lng: parseFloat(stringCoordinates.split(',')[1]),
    });
  }, [validation]);

  const onChangeLandmards = React.useCallback((e) => {
    validation.setFieldValue('landmarks', e, true);
  }, [validation]);

  const isDisabledForExtranet = React.useCallback(
    (fieldDisabled = false) => (isExtranet ? true : fieldDisabled),
    [isExtranet],
  );

  return (
    <div className={styles.Component}>
      <Modal
        fade
        size="lg"
        isOpen={isOpenModalEditLocation}
        toggle={onToggleModal}
        className={styles.Modal}
      >
        <form onSubmit={validation.handleSubmit}>
          <ModalHeader
            toggle={onToggleModal}
            className={styles.ModalHeader}
            close={CloseButton}
          />

          <ModalBody className={styles.ModalBody}>
            <Label className="Label">
              Điền thông tin Chỗ nghỉ của Quý vị
            </Label>

            <hr className="Divider" />
            <FormField
              isHiddenLabel
            >

              {/* Name */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Tên chỗ nghỉ
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <AppInputValidation
                    isShowCounter
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Nhập vào chỗ nghỉ"
                    validation={validation}
                    isDisabled={isDisabledForExtranet()}
                  />
                </Col>
              </Row>

              {/* Type */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Loại hình
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <SelectValidation
                    name="placeType"
                    keyName="placeType"
                    placeholder="Chọn loại hình"
                    options={placeTypeOptions}
                    validation={validation}
                    onChange={onChangeOption('placeType')}
                    value={validation.values.placeType}
                    fontSize="13px"
                    isShowError
                    isDisabled={isDisabledForExtranet()}
                  />
                </Col>
              </Row>

              {/* Rank Star */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Hạng sao
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <SelectValidation
                    name="starRating"
                    keyName="starRating"
                    placeholder="Chọn hạng sao"
                    options={starOptions}
                    validation={validation}
                    onChange={onChangeOption('starRating')}
                    value={validation.values.starRating}
                    fontSize="13px"
                    isDisabled={isDisabledForExtranet()}
                  />
                </Col>
              </Row>

              {/* Province  */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Tỉnh/ Thành phố
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <SelectValidation
                    name="province"
                    keyName="province"
                    placeholder="Chọn thành phố"
                    isHiddenSearchable
                    options={formattedProvinces}
                    validation={validation}
                    onChange={onChangeOption('province')}
                    value={validation.values.province}
                    fontSize="13px"
                    isDisabled={isDisabledForExtranet()}
                  />
                </Col>
              </Row>

              {/* Market */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Thị trường
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <SelectValidation
                    name="market"
                    keyName="market"
                    placeholder="Vui lòng chọn thị trường"
                    isHiddenSearchable
                    options={marketOptions}
                    validation={validation}
                    value={selectedMarketOption}
                    isUsePureValue
                    onChange={onChangeMarketOption}
                    fontSize="13px"
                    isDisabled={isDisabledForExtranet(!validation.values.province)}
                  />
                </Col>
              </Row>

              {/* District  */}
              {/* <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Quận/ Huyện
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <SelectValidation
                    name="district"
                    keyName="district"
                    placeholder="Chọn Quận/ Huyện"
                    isHiddenSearchable
                    options={formattedDistricts}
                    validation={validation}
                    onChange={onChangeOption('district')}
                    value={validation.values.district}
                    fontSize="13px"
                    isDisabled={isDisabledForExtranet(!validation.values.province)}
                  />
                </Col>
              </Row> */}

              {/* Ward  */}
              {/* <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Phường/ Xã
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <SelectValidation
                    name="ward"
                    keyName="ward"
                    placeholder="Chọn Phường/ Xã"
                    isHiddenSearchable
                    options={formattedWards}
                    validation={validation}
                    onChange={onChangeOption('ward')}
                    value={validation.values.ward}
                    fontSize="13px"
                    isDisabled={isDisabledForExtranet(!validation.values.district)}
                  />
                </Col>
              </Row> */}

              {/* landmarks  */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Khu vực
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <SelectValidation
                    placeholder="Vui lòng chọn khu vực"
                    options={landmarkOptions}
                    value={validation.values.landmarks}
                    name="landmarks"
                    validation={validation}
                    onChangeMulti={onChangeLandmards}
                    isMulti
                    isHiddenClearable
                    isDisabled={isDisabledForExtranet(!validation.values.market)}
                    isLoading={isLoadingLandmarks}
                  />
                </Col>
              </Row>

              {/* Address  */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Địa chỉ
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <AppInputValidation
                    isShowCounter
                    name="address"
                    id="address"
                    type="text"
                    placeholder="Nhập vào địa chỉ"
                    validation={validation}
                    limitCharacter={200}
                    isDisabled={isDisabledForExtranet()}
                  />
                </Col>
              </Row>

              {/* Coordinates  */}
              <Row className="FormFieldCoordinates">
                <Col
                  md="4"
                  className="FormFieldCoordinates__Title"
                >
                  Định vị
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <AppInputValidation
                    name="coordinates"
                    id="coordinates"
                    type="text"
                    placeholder="Nhập vào định vị"
                    validation={validation}
                    isCustomOnChange
                    onChange={onChangeCoordinates}
                    isDisabled={isDisabledForExtranet()}
                  />

                  <div className="text-start">
                    Trên
                    {' '}
                    <button
                      type="button"
                      onClick={toggleMapModal}
                      className={styles.Hyperlink}
                      style={{
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        outline: 'none',
                      }}
                    >
                      Google Maps
                    </button>
                  </div>
                </Col>
              </Row>

              {/* Description  */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  Miêu tả chỗ nghỉ
                  {' '}
                  <code>*</code>
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <AppInputValidation
                    isShowCounter
                    inputClassName="FormField__Textarea"
                    name="description"
                    id="description"
                    type="textarea"
                    placeholder="Nhập vào mô tả"
                    validation={validation}
                    limitCharacter={1500}
                  />
                </Col>
              </Row>

              {/* OTA Hotel Id */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  OTA Hotel Id
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <AppInputValidation
                    name="otaHotelId"
                    id="otaHotelId"
                    type="text"
                    placeholder="Nhập OTA Hotel Id"
                    validation={validation}
                  />
                </Col>
              </Row>

              {/* OTA Hotel Channel Key */}
              <Row className="FormField">
                <Col
                  md="4"
                  className="FormField__Title"
                >
                  OTA Hotel Channel Key
                </Col>

                <Col
                  md="8"
                  className="FormField__Input__Wrapper"
                >
                  <AppInputValidation
                    name="otaHotelChannelKey"
                    id="otaHotelChannelKey"
                    type="text"
                    placeholder="Nhập OTA Hotel Channel Key"
                    validation={validation}
                  />
                </Col>
              </Row>
            </FormField>
          </ModalBody>

          <ModalFooter className={styles.ModalFooter}>
            <div className="d-flex m-0">
              <Button
                classes="Button Button__Cancel"
                onClick={onToggleModal}
              >
                Hủy
              </Button>

              <Button
                classes=" Button Button__Submit"
                type="submit"
                loading={isLoading}
              >
                Gửi
              </Button>
            </div>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={isMapModalOpen} toggle={toggleMapModal} size="lg" centered>
        <ModalHeader toggle={toggleMapModal}>Chọn tọa độ trên bản đồ</ModalHeader>
        <ModalBody>
          <div className={styles.MapModalContent}>
            <CoordinateMap
              defaultPosition={validation.values.coordinates}
              validation={validation}
              mapRef={mapRef}
            />
          </div>
          <div className="d-flex justify-content-end mt-3">
            <Button type="button" onClick={toggleMapModal}>
              Xác nhận
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

ModalEditLocation.propTypes = {
  isOpenModalEditLocation: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,

  validation: PropTypes.instanceOf(Object).isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  // districts: PropTypes.instanceOf(Array).isRequired,
  // wards: PropTypes.instanceOf(Array).isRequired,

  onToggleModal: PropTypes.func.isRequired,
};

ModalEditLocation.defaultProps = {
};

export default React.memo(ModalEditLocation);
